/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * Public photos on an owner review (contract-review-photos): the pure half — reading `images`
 * off any note DealHunt sends (absent = none), the picker's tiles (the discussions writer's
 * `DraftImage`, so `ImagesBlock` / `PhotoTile` draw them unchanged), what Publish sends as
 * `media_ids`, the reader's lines, and the tab's stored set. No fetch but `uploadReviewPhoto`,
 * no React; unit-tested in `tests/review-photos-block.spec.tsx`.
 *
 * Photos are PUBLIC and SIGNED-IN ONLY: they upload at pick time with the session's bearer
 * (`/api/owner-notes/reviews/media`), and a publish that cannot resolve to a signed profile never
 * carries `media_ids` (DealHunt would answer 400 `photos_need_sign_in`).
 */
import { OWNER_REVIEW_PHOTOS_MAX, type NoteImage } from 'lib/dealhunt/types'
import { fileRefusal, PHOTO_DRAFT_TTL_MS, type DraftImage } from 'lib/community/discussions-writer'
import { imageSource } from 'lib/community/discussions'
import type { DiscussionMediaResponse } from 'lib/community/discussions-types'
import { safeImageUrl } from 'lib/community/model'
import type { OwnerCredential } from './auth-bridge'
import { uploadPhoto, type KeyedUpload } from './keyed-api'

export { OWNER_REVIEW_PHOTOS_MAX as REVIEW_PHOTOS_MAX }

/** Photos uploading at once (the discussions writer's number). */
export const REVIEW_PHOTO_SLOTS = 3

/** One picker tile — the discussions writer's shape, so the same tile and grid draw it. */
export type ReviewPhoto = DraftImage

// ── reading DealHunt's notes ─────────────────────────────────────────────────

/** A note's public photos, https only, in DealHunt's order; `[]` when absent or not a list (an
 *  older DealHunt, an aggregate row stored before photos). */
export function noteImages(note: { images?: unknown } | null | undefined): NoteImage[] {
  const raw = note?.images
  if (!Array.isArray(raw)) {
    return []
  }
  return (raw as unknown[]).flatMap(item => {
    const im = item as Partial<NoteImage> | null
    const url = safeImageUrl(typeof im?.url === 'string' ? im.url : null)
    if (!im || !url || typeof im.media_id !== 'string' || !im.media_id) {
      return []
    }
    return [
      {
        media_id: im.media_id,
        url,
        width: typeof im.width === 'number' ? im.width : 0,
        height: typeof im.height === 'number' ? im.height : 0,
        ...(im.variants ? { variants: im.variants } : {}),
        ...(typeof im.alt === 'string' && im.alt.trim() ? { alt: im.alt.trim() } : {}),
      },
    ]
  })
}

/** How many photos the review has: DealHunt's `image_count` when sent and not below what it
 *  printed, else the printed photos. */
export function noteImageCount(note: { images?: unknown; image_count?: unknown }): number {
  const shown = noteImages(note).length
  const sent = note.image_count
  return typeof sent === 'number' && Number.isInteger(sent) && sent >= shown ? sent : shown
}

/** The alt text: the owner's own when DealHunt sends one, else "<product> — owner photo". */
export function photoAlt(image: Pick<NoteImage, 'alt'>, productName: string | null): string {
  if (image.alt) {
    return image.alt
  }
  return productName ? `${productName} — owner photo` : 'Owner photo'
}

// ── the picker ───────────────────────────────────────────────────────────────

/** A note's photos as already-uploaded tiles (edit mode): attached, so never expired. */
export function photosFromImages(images: readonly NoteImage[]): ReviewPhoto[] {
  return images.map(im => ({
    key: im.media_id,
    status: 'done',
    progress: 1,
    media_id: im.media_id,
    url: im.url,
    width: im.width,
    height: im.height,
    error: null,
    uploaded_at: null,
    preview_url: imageSource(im, 'thumb', 'card')?.src ?? null,
  }))
}

/** A tile uploaded more than `PHOTO_DRAFT_TTL_MS` ago: DealHunt may have collected it (24 h GC
 *  of unattached media). Attached tiles (`uploaded_at` null) never expire. */
export function photoExpired(photo: ReviewPhoto, now: number): boolean {
  return photo.uploaded_at !== null && now - photo.uploaded_at > PHOTO_DRAFT_TTL_MS
}

/** The media ids a publish can attach, in tile order: uploaded and not expired. */
export function sendableMediaIds(photos: readonly ReviewPhoto[], now: number): string[] {
  const ids: string[] = []
  photos.forEach(p => {
    if (p.status === 'done' && p.media_id && !photoExpired(p, now)) {
      ids.push(p.media_id)
    }
  })
  return ids
}

/** A tile is still on its way up: Publish waits for it. */
/**
 * A dismissed sign-in: the tiles still `queued` (never started — they were waiting for a session)
 * leave the set; uploading, done and failed tiles stay. Pure; `useReviewPhotos` frees their files.
 */
export function dropQueuedPhotos(photos: readonly ReviewPhoto[]): {
  kept: ReviewPhoto[]
  dropped: string[]
} {
  const dropped = photos.filter(p => p.status === 'queued').map(p => p.key)
  return { kept: photos.filter(p => p.status !== 'queued'), dropped }
}

export function photosUploading(photos: readonly ReviewPhoto[]): boolean {
  return photos.some(p => p.status === 'queued' || p.status === 'uploading')
}

/**
 * What the body carries as `media_ids` (§2, PATCH: absent keeps, a list replaces, `[]` removes
 * all): the current ids when they differ from what DealHunt last got (`baseline` — `[]` for a new
 * review, the note's photos in edit mode, the last sent set after a send), else undefined.
 */
export function mediaIdsToSend(
  current: readonly string[],
  baseline: readonly string[]
): string[] | undefined {
  const same = current.length === baseline.length && current.every((id, i) => id === baseline[i])
  return same ? undefined : [...current]
}

/**
 * What a publish does with the photo set, decided AFTER the link step (and again after a device
 * re-mint): `signed` = this send resolves to a signed profile — the device was already claimed,
 * or the claim at Publish just answered `linked`. A failed link (network, 5xx) or a freshly
 * minted device is not signed: the review goes WITHOUT `media_ids` and `dropped` says the set
 * the reader changed did not attach — never a refused review for its photos.
 */
export function photoSendPlan(args: {
  signed: boolean
  photos: readonly ReviewPhoto[]
  baseline: readonly string[]
  now: number
}): { mediaIds: string[] | undefined; dropped: boolean } {
  const wanted = mediaIdsToSend(sendableMediaIds(args.photos, args.now), args.baseline)
  if (args.signed) {
    return { mediaIds: wanted, dropped: false }
  }
  return { mediaIds: undefined, dropped: wanted !== undefined }
}

/** Uploaded tiles past the 23 h window (DealHunt may have collected them): Publish marks them
 *  failed — "Pick this photo again" — instead of dropping them unseen. */
export function expiredPhotoKeys(photos: readonly ReviewPhoto[], now: number): string[] {
  return photos.filter(p => p.status === 'done' && photoExpired(p, now)).map(p => p.key)
}

export const PICK_AGAIN = 'Pick this photo again'

export function expiredPhotosLine(n: number): string {
  return n === 1
    ? 'One photo was uploaded over a day ago and has expired — pick it again or remove it, then publish.'
    : `${n} photos were uploaded over a day ago and have expired — pick them again or remove them, then publish.`
}

/**
 * The queued tiles the upload queue starts now: none without a usable session (an upload needs
 * the bearer — Constraint 5599e215), else queued tiles in order up to the free slots.
 */
export function uploadsToStart(
  photos: readonly ReviewPhoto[],
  args: { usable: boolean; inFlight: number; slots?: number }
): string[] {
  if (!args.usable) {
    return []
  }
  const free = Math.max(0, (args.slots ?? REVIEW_PHOTO_SLOTS) - args.inFlight)
  return photos
    .filter(p => p.status === 'queued')
    .slice(0, free)
    .map(p => p.key)
}

/** The picked files that fit: allowed types and sizes (the discussions writer's rule), up to the
 *  room left; the rest counted for the line. */
export function acceptFiles(
  files: readonly File[],
  room: number
): { accepted: File[]; skipped: { type: number; size: number; room: number } } {
  const skipped = { type: 0, size: 0, room: 0 }
  const accepted: File[] = []
  files.forEach(file => {
    const refusal = fileRefusal(file)
    if (refusal) {
      skipped[refusal] += 1
    } else if (accepted.length < room) {
      accepted.push(file)
    } else {
      skipped.room += 1
    }
  })
  return { accepted, skipped }
}

/** The line under the picker after a pick left files out ('' when none was). */
export function reviewSkippedLine(skipped: { type: number; size: number; room: number }): string {
  const parts: string[] = []
  if (skipped.type) {
    parts.push(`${skipped.type} not a supported photo (GIF and SVG are not)`)
  }
  if (skipped.size) {
    parts.push(`${skipped.size} over 4 MB`)
  }
  if (skipped.room) {
    parts.push(`${skipped.room} over the limit of ${OWNER_REVIEW_PHOTOS_MAX} photos`)
  }
  return parts.length ? `Left out: ${parts.join(', ')}.` : ''
}

// ── the reader's lines ───────────────────────────────────────────────────────

export const PHOTOS_COPY = {
  legend: 'Photos of yours (optional)',
  fine: `Up to ${OWNER_REVIEW_PHOTOS_MAX}. They are public: they show with your review.`,
  signIn: 'Sign in to add photos.',
  /** Photos on the tiles that this publish cannot carry (no signed profile). */
  leftOut: 'Sign in and link your account to publish these photos — without it they are left out.',
  uploading: 'Photos still uploading…',
  /** The review went, its photo changes did not (no signed profile at send time). */
  dropped:
    "Your review was sent without the photos — they didn't attach. Sign in, then add them from My gear.",
} as const

/** DealHunt's photo refusals on a publish / edit (§2), as the band's error line. */
export const PHOTO_ERROR_LINES: Readonly<Record<string, string>> = {
  photos_need_sign_in:
    'Photos need your account: sign in and link it, or remove the photos and publish.',
  photos_too_many: `Up to ${OWNER_REVIEW_PHOTOS_MAX} photos.`,
  media_invalid: "A photo didn't go through, remove it and try again.",
  photos_unavailable: "Photos can't be added right now, publish without them or try later.",
}

export function photoErrorLine(code: string): string | null {
  return PHOTO_ERROR_LINES[code] ?? null
}

// ── the tab's stored set (the community writer, a new review only) ───────────

/** Uploaded tiles as stored: the media and its upload time, never a local file. */
export function serializePhotos(photos: readonly ReviewPhoto[]): string | null {
  const kept = photos.filter(p => p.status === 'done' && p.media_id && p.url)
  if (!kept.length) {
    return null
  }
  return JSON.stringify(
    kept.map(p => ({
      media_id: p.media_id,
      url: p.url,
      width: p.width,
      height: p.height,
      uploaded_at: p.uploaded_at,
    }))
  )
}

/** A stored set as tiles: https only, uploaded inside the 23 h window, at most the limit. */
export function parseStoredPhotos(raw: string | null, now: number): ReviewPhoto[] {
  if (!raw) {
    return []
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) {
    return []
  }
  return (parsed as unknown[])
    .flatMap(item => {
      const p = item as Record<string, unknown> | null
      const url = safeImageUrl(typeof p?.url === 'string' ? p.url : null)
      const uploaded = p?.uploaded_at
      if (
        !p ||
        !url ||
        typeof p.media_id !== 'string' ||
        !p.media_id ||
        typeof uploaded !== 'number' ||
        !Number.isFinite(uploaded)
      ) {
        return []
      }
      const tile: ReviewPhoto = {
        key: p.media_id,
        status: 'done',
        progress: 1,
        media_id: p.media_id,
        url,
        width: typeof p.width === 'number' ? p.width : 0,
        height: typeof p.height === 'number' ? p.height : 0,
        error: null,
        uploaded_at: uploaded,
      }
      return photoExpired(tile, now) ? [] : [tile]
    })
    .slice(0, OWNER_REVIEW_PHOTOS_MAX)
}

// ── the upload ───────────────────────────────────────────────────────────────

export type ReviewMedia = DiscussionMediaResponse['media']

/** One photo to `/api/owner-notes/reviews/media` (§1), with progress — the keyed XHR upload the
 *  discussions writer and Help & support use. */
export function uploadReviewPhoto(
  credential: OwnerCredential,
  file: File,
  onProgress: (fraction: number) => void
): KeyedUpload<ReviewMedia> {
  return uploadPhoto('/reviews/media', credential, file, onProgress, body => {
    const media = (body as Partial<DiscussionMediaResponse> | null)?.media
    return media && typeof media.id === 'string' && typeof media.url === 'string' ? media : null
  })
}
